import { Grid, GridColumn, GridRow, Table, TableColumns, Tooltip, useDefaultSelecting } from '@bp/ui-components';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { use_PublishedSubstitutionsQuery } from '../../../types/planung-graphql-client-defs';

type PublishedSubstitutionsTableType = {
  uuid: string;
  date: string;
  lastPublished: string;
  teachers: string;
  classes: string;
  substitutions: string;
  notifications: string;
};

export const PublishedSubstitutionsTable = observer(() => {
  const schoolYear = useSelectedSchoolYear();

  const [{ data }] = use_PublishedSubstitutionsQuery({
    variables: { start: schoolYear?.start ?? '', end: schoolYear?.end ?? '' },
    pause: !schoolYear,
  });

  const publishedSubstitutionsColumns: TableColumns<PublishedSubstitutionsTableType>[] = [
    {
      id: 'date',
      accessorKey: 'date',
      header: 'date',
      cell: ({ row }) => dayjs(row.original.date).format('DD.MM.YYYY'),
      size: 80,
    },
    {
      id: 'lastPublished',
      accessorKey: 'lastPublished',
      header: 'lastPublished',
      cell: ({ row }) => dayjs(row.original.lastPublished).toISOString(),
      size: 220,
    },
    {
      id: 'teachers',
      header: 'teachers',
      accessorKey: 'teachers',
      cell: ({ row }) => (
        <Tooltip content={<pre>{JSON.stringify(JSON.parse(row.original.teachers), null, 2)}</pre>}>
          {row.original.teachers}
        </Tooltip>
      ),
      size: 200,
    },
    {
      id: 'classes',
      header: 'classes',
      accessorKey: 'classes',
      cell: ({ row }) => (
        <Tooltip content={<pre>{JSON.stringify(JSON.parse(row.original.classes), null, 2)}</pre>}>
          {row.original.classes}
        </Tooltip>
      ),
      size: 200,
    },
    {
      id: 'substitutions',
      header: 'substitutions',
      accessorKey: 'substitutions',
      cell: ({ row }) => (
        <Tooltip content={<pre>{JSON.stringify(JSON.parse(row.original.substitutions), null, 2)}</pre>}>
          {row.original.substitutions}
        </Tooltip>
      ),
      size: 300,
    },
    {
      id: 'notifications',
      header: 'notifications',
      accessorKey: 'notifications',
      cell: ({ row }) => (
        <Tooltip content={<pre>{JSON.stringify(JSON.parse(row.original.notifications), null, 2)}</pre>}>
          {row.original.notifications}
        </Tooltip>
      ),

      size: 200,
    },
  ];

  const eventJobData: PublishedSubstitutionsTableType[] = useMemo(() => {
    return data?.publishedSubstitutionDays.map((d) => ({ ...d, lastPublished: d.lastPublished ?? '' })) ?? [];
  }, [data]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  return (
    <Grid>
      <GridRow headline={'Veröffentlichte Tage'}>
        <GridColumn width={12}>
          <Table
            isOnWhite={false}
            showSort={true}
            showActionBar={true}
            sorting={sorting}
            showSelect={true}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            onSortingChange={setSorting}
            actionBarSettings={{
              showSelectedRowsCount: true,
              extendedActionsRight: <></>,
            }}
            columns={publishedSubstitutionsColumns}
            data={eventJobData}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
});
